import { useCallback, useState } from 'react';
import { AISuggestionState } from '../types';
import { stanNotify } from '@/lib';
import { EditorActions } from '../editor.logic';

export const useAiGenerate = ({
    editorActions,
    onAiGenerate,
}: {
    editorActions: EditorActions;
    onAiGenerate?: (prompt: string) => Promise<string>;
}) => {
    const [aiSuggestionState, setAiSuggestionState] = useState<AISuggestionState>('idle');
    const [aiSuggestion, setAiSuggestion] = useState('');
    const [aiPrompt, setAiPrompt] = useState('');

    const handleAiGenerate = useCallback(
        async (prompt: string) => {
            if (!onAiGenerate) return;
            setAiSuggestionState('loading');
            setAiPrompt(prompt);
            try {
                const aiSuggestion = await onAiGenerate(prompt);
                setAiSuggestion(aiSuggestion);
                setAiSuggestionState('success');
            } catch {
                setAiSuggestionState('error');
                stanNotify({
                    type: 'error',
                    title: 'Failed to reach Stanley.',
                    text: 'Please try again',
                    duration: 5000,
                });
                resetAiSuggestion();
            }
        },
        [onAiGenerate]
    );

    const handleAiSuggestionAccept = () => {
        editorActions.setContent(aiSuggestion);
        resetAiSuggestion();
    };

    const handleAiSuggestionDismiss = () => {
        resetAiSuggestion();
    };

    const handleAiSuggestionRetry = async () => {
        await handleAiGenerate(aiPrompt);
    };

    const resetAiSuggestion = () => {
        setAiSuggestionState('idle');
        setAiSuggestion('');
    };

    return {
        aiSuggestionState,
        aiSuggestion,
        aiPrompt,
        handleAiGenerate,
        handleAiSuggestionAccept,
        handleAiSuggestionDismiss,
        handleAiSuggestionRetry,
    };
};
